'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { MediaAuthorArticleListItem } from '@/features/media-authors/types';
import { getMediaAuthorArticles } from '@/features/media-authors/utils/media-authors-api.utils';

type UseMediaAuthorArticlesInput = {
  readonly slug: string;
  readonly initialDocs: MediaAuthorArticleListItem[];
  readonly initialHasNextPage: boolean;
  readonly limit?: number;
};

type UseMediaAuthorArticlesResult = {
  readonly articles: MediaAuthorArticleListItem[];
  readonly isLoading: boolean;
  readonly hasNextPage: boolean;
  readonly loadMore: () => void;
  readonly sentinelRef: (node: HTMLDivElement | null) => void;
};

const DEFAULT_LIMIT = 12;

/**
 * Infinite scroll loader for an author's published articles.
 */
export function useMediaAuthorArticles(
  input: UseMediaAuthorArticlesInput,
): UseMediaAuthorArticlesResult {
  const limit = input.limit ?? DEFAULT_LIMIT;
  const [articles, setArticles] = useState(input.initialDocs);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(input.initialHasNextPage);
  const [isLoading, setIsLoading] = useState(false);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);

  const loadMore = useCallback((): void => {
    if (isLoading || !hasNextPage) {
      return;
    }
    setIsLoading(true);
    const nextPage = page + 1;
    void getMediaAuthorArticles({ slug: input.slug, page: nextPage, limit })
      .then((result) => {
        setArticles((prev) => [...prev, ...result.docs]);
        setPage(nextPage);
        setHasNextPage(result.hasNextPage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [hasNextPage, input.slug, isLoading, limit, page]);

  const sentinelRef = useCallback((node: HTMLDivElement | null): void => {
    sentinelNodeRef.current = node;
  }, []);

  useEffect(() => {
    const node = sentinelNodeRef.current;
    if (!node || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, loadMore, articles.length]);

  return {
    articles,
    isLoading,
    hasNextPage,
    loadMore,
    sentinelRef,
  };
}
